/*
 * @Copyright 2026. Institute for Future Intelligence, Inc.
 *
 * "MMFF-lite" atom typer for the classical molecular force field.
 *
 * WHY THIS EXISTS. The default FF gives every radial bond the same stiffness
 * (RadialBond.DEFAULT_STRENGTH = 10 eV/Å²) and every angle the same bending
 * stiffness, regardless of chemistry — so a C–H, a C=O, and an aromatic C–C
 * all vibrate at the same frequency. Real force constants depend on the
 * *atom types* of the two bonded atoms, not just their elements: a C–C single
 * bond between two sp³ carbons is much softer than a C=C between two sp²
 * carbons, which is softer than an aromatic ring C–C. That neighbor
 * dependence is exactly why proper force fields (MMFF94, GAFF, OPLS) key their
 * bond/angle tables on atom types rather than element pairs.
 *
 * Full MMFF94 perception assigns ~100 numeric atom types from hybridization,
 * aromaticity, formal charge, and multi-shell neighbor context. We don't need
 * that resolution to fix the headline problem (one-stiffness-fits-all). This
 * module assigns a coarse, SYBYL-style type — element + hybridization, with an
 * aromatic refinement — perceived purely from local connectivity (coordination
 * number + bond orders), which is information AIMS already carries on its
 * RadialBond list. The same element pair in two different environments lands in
 * two different *type* buckets automatically, because the thing that makes them
 * different (the atoms' hybridization) is what we type on.
 *
 * Types emitted (string tags, SYBYL-ish):
 *   H                      — hydrogen (and any monovalent attachment)
 *   C.3 / C.2 / C.1 / C.ar — sp³ / sp² / sp / aromatic carbon
 *   N.3 / N.2 / N.1 / N.ar — likewise for nitrogen
 *   O.3 / O.2              — sp³ (ether/hydroxyl/water) / sp² (carbonyl) oxygen
 *   <symbol>               — any other element, bare (always falls through to
 *                            the parameter-table default — see mmffParamLookup)
 *
 * LIMITS (prototype scope, documented honestly):
 *   • Aromaticity is a ring-of-sp² heuristic (5–6-membered rings whose members
 *     all carry a double bond in the Kekulé structure). It catches benzene and
 *     pyridine; it MISSES lone-pair aromatics whose ring atom has no double
 *     bond (furan O, pyrrole N) because those perceive as sp³. Good enough to
 *     demonstrate the typer; refine when a validation case needs it.
 *   • Coarse typing merges environments MMFF would split (carbonyl C vs
 *     carboxylate C). Those collapse to one bucket and lose a little accuracy;
 *     the fallback chain in mmffParamLookup keeps them no worse than today.
 *   • Bond orders must actually be set on the RadialBond list. The dynamics
 *     path currently constructs bonds without propagating order (only
 *     polymerBuilder does) — without that, every atom perceives as sp³. Wiring
 *     Regime A in production means propagating chem-lib bond order → rb.type at
 *     the construction sites.
 */

import { Atom } from './Atom.ts';
import { BondType } from '../constants.ts';

// Minimal structural view of a radial bond — RadialBond satisfies this, but so
// does any {atom1, atom2, type} triple, which keeps the typer testable without
// constructing full engine objects.
export interface TypingBond {
  atom1: Atom;
  atom2: Atom;
  type: BondType;
}

// Integer bond order from the BondType tag. Hydrogen/vdW bonds (which never
// appear in the bonded topology the typer sees) are treated as order 1.
export const bondOrderOf = (t: BondType): number =>
  t === BondType.DOUBLE_BOND ? 2 : t === BondType.TRIPLE_BOND ? 3 : 1;

const ORGANIC = new Set(['C', 'N', 'O']);

interface Neighborhood {
  neighbors: Atom[];
  orders: number[]; // bond order to each neighbor, index-aligned with neighbors
  maxOrder: number;
  doubleCount: number;
}

// Build per-atom adjacency (neighbors + the bond order to each) from the bond
// list. Only real σ-framework bonds matter for typing.
const buildNeighborhoods = (atoms: Atom[], bonds: TypingBond[]): Map<Atom, Neighborhood> => {
  const map = new Map<Atom, Neighborhood>();
  for (const a of atoms) map.set(a, { neighbors: [], orders: [], maxOrder: 0, doubleCount: 0 });
  for (const b of bonds) {
    const order = bondOrderOf(b.type);
    const n1 = map.get(b.atom1);
    const n2 = map.get(b.atom2);
    if (n1) {
      n1.neighbors.push(b.atom2);
      n1.orders.push(order);
      n1.maxOrder = Math.max(n1.maxOrder, order);
      if (order === 2) n1.doubleCount++;
    }
    if (n2) {
      n2.neighbors.push(b.atom1);
      n2.orders.push(order);
      n2.maxOrder = Math.max(n2.maxOrder, order);
      if (order === 2) n2.doubleCount++;
    }
  }
  return map;
};

// Hybridization digit from local bonding: a triple bond or two cumulated
// double bonds ⇒ sp (1); one double bond ⇒ sp² (2); otherwise sp³ (3).
const hybridization = (nh: Neighborhood): 1 | 2 | 3 => {
  if (nh.maxOrder >= 3 || nh.doubleCount >= 2) return 1;
  if (nh.doubleCount === 1) return 2;
  return 3;
};

// Mark aromatic atoms: members of a 5- or 6-membered ring in which every atom
// is sp² and of an aromatic-capable element (C or N here). Bounded DFS over the
// sp² subgraph — cheap for the small/medium molecules the molecular engine
// handles, and started only from not-yet-marked atoms to avoid redundant work.
const findAromatic = (atoms: Atom[], nbh: Map<Atom, Neighborhood>, hyb: Map<Atom, number>): Set<Atom> => {
  const aromatic = new Set<Atom>();
  const sp2 = (a: Atom) => hyb.get(a) === 2 && (a.elementSymbol === 'C' || a.elementSymbol === 'N');
  const sp2Neighbors = (a: Atom): Atom[] => (nbh.get(a)?.neighbors ?? []).filter(sp2);

  for (const start of atoms) {
    if (!sp2(start) || aromatic.has(start)) continue;
    const path: Atom[] = [start];
    const inPath = new Set<Atom>([start]);
    const recurse = (current: Atom): boolean => {
      for (const nb of sp2Neighbors(current)) {
        if (nb === start && path.length >= 5 && path.length <= 6) {
          for (const a of path) aromatic.add(a);
          return true;
        }
        if (!inPath.has(nb) && path.length < 6) {
          path.push(nb);
          inPath.add(nb);
          if (recurse(nb)) return true;
          path.pop();
          inPath.delete(nb);
        }
      }
      return false;
    };
    recurse(start);
  }
  return aromatic;
};

/**
 * Perceive a coarse atom type for every atom, returned as a Map keyed by the
 * Atom instance (atom.index is not assumed contiguous across all callers).
 */
export const perceiveAtomTypes = (atoms: Atom[], bonds: TypingBond[]): Map<Atom, string> => {
  const nbh = buildNeighborhoods(atoms, bonds);
  const hyb = new Map<Atom, number>();
  for (const a of atoms) hyb.set(a, hybridization(nbh.get(a)!));
  const aromatic = findAromatic(atoms, nbh, hyb);

  const types = new Map<Atom, string>();
  for (const a of atoms) {
    const el = a.elementSymbol;
    if (el === 'H') {
      types.set(a, 'H');
    } else if (ORGANIC.has(el)) {
      if (aromatic.has(a)) types.set(a, `${el}.ar`);
      else types.set(a, `${el}.${hyb.get(a)}`);
    } else {
      types.set(a, el); // bare element ⇒ no pack entry ⇒ default stiffness
    }
  }
  return types;
};
